import React, { useState, useEffect } from 'react';
import { Card, Input, Select, Tag, Space, Spin, Empty, Typography, Row, Col } from '@douyinfe/semi-ui';
import { IconSearch } from '@douyinfe/semi-icons';
import { API } from '../../helpers';

const { Title, Text } = Typography;

const Models = () => {
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedBilling, setSelectedBilling] = useState('all');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchModels();
    fetchProviders();
  }, []);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const res = await API.get('/api/models/pricing');
      if (res.data.success) {
        setModels(res.data.data || []);
        setFilteredModels(res.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch models:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const res = await API.get('/api/models/providers');
      if (res.data.success) {
        setProviders(res.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    }
  };

  useEffect(() => {
    filterModels();
  }, [searchText, selectedProvider, selectedType, selectedBilling, models]);

  const filterModels = () => {
    let filtered = models;
    
    if (searchText) {
      filtered = filtered.filter(m =>
        m.model_name.toLowerCase().includes(searchText.toLowerCase()) ||
        m.description?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    if (selectedProvider !== 'all') {
      filtered = filtered.filter(m => m.provider === selectedProvider);
    }
    
    if (selectedType !== 'all') {
      filtered = filtered.filter(m => m.endpoint_type === selectedType);
    }
    
    if (selectedBilling !== 'all') {
      filtered = filtered.filter(m => m.billing_type === selectedBilling);
    }
    
    setFilteredModels(filtered);
  };

  const getBillingTypeText = (type) => {
    const typeMap = {
      'token': '按 Token',
      'request': '按次',
      'second': '按时长'
    };
    return typeMap[type] || type;
  };

  const getBillingTypeColor = (type) => {
    const colorMap = {
      'token': 'blue',
      'request': 'green',
      'second': 'orange'
    };
    return colorMap[type] || 'grey';
  };

  return (
    <div style={{ padding: '24px', background: '#f5f7fa', minHeight: 'calc(100vh - 60px)' }}>
      {/* 页面标题区域 - 参考首页风格 */}
      <div style={{ 
        marginBottom: '24px',
        padding: '24px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px',
        color: '#fff'
      }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600' }}>
          📊 模型定价
        </h2>
        <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
          查看所有可用模型及其价格，支持多种筛选和搜索方式
        </p>
      </div>

      {/* 搜索和筛选区域 */}
      <Card style={{ 
        marginBottom: '24px', 
        borderRadius: '8px',
        border: '1px solid #e4e7ed',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Input
            prefix={<IconSearch />}
            placeholder="搜索模型名称、描述或供应商..."
            value={searchText}
            onChange={setSearchText}
            size="large"
            style={{ borderRadius: '8px' }}
          />
          <Row gutter={16}>
            <Col span={8}>
              <Select
                value={selectedProvider}
                onChange={setSelectedProvider}
                style={{ width: '100%', borderRadius: '8px' }}
                placeholder="选择供应商"
              >
                <Select.Option value="all">所有供应商</Select.Option>
                {providers.map(p => (
                  <Select.Option key={p} value={p}>{p}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <Select
                value={selectedType}
                onChange={setSelectedType}
                style={{ width: '100%', borderRadius: '8px' }}
                placeholder="选择类型"
              >
                <Select.Option value="all">所有类型</Select.Option>
                <Select.Option value="openai">OpenAI</Select.Option>
                <Select.Option value="anthropic">Anthropic</Select.Option>
                <Select.Option value="gemini">Gemini</Select.Option>
                <Select.Option value="image-generation">图片生成</Select.Option>
              </Select>
            </Col>
            <Col span={8}>
              <Select
                value={selectedBilling}
                onChange={setSelectedBilling}
                style={{ width: '100%', borderRadius: '8px' }}
                placeholder="计费方式"
              >
                <Select.Option value="all">全部</Select.Option>
                <Select.Option value="token">按 Token 计费</Select.Option>
                <Select.Option value="request">按次计费</Select.Option>
                <Select.Option value="second">按时长计费</Select.Option>
              </Select>
            </Col>
          </Row>
        </div>
      </Card>

      {/* 模型列表 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size="large" />
        </div>
      ) : filteredModels.length === 0 ? (
        <Card style={{ 
          borderRadius: '8px',
          border: '1px solid #e4e7ed',
          textAlign: 'center',
          padding: '80px 0'
        }}>
          <Empty description="没有找到匹配的模型" />
        </Card>
      ) : (
        <div>
          {/* 统计信息 */}
          <div style={{ 
            marginBottom: '16px',
            padding: '12px 16px',
            background: '#fff',
            borderRadius: '8px',
            border: '1px solid #e4e7ed',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '14px', color: '#606266' }}>
              共找到 <strong style={{ color: '#303133' }}>{filteredModels.length}</strong> 个模型
            </span>
            {(searchText || selectedProvider !== 'all' || selectedType !== 'all' || selectedBilling !== 'all') && (
              <span 
                style={{ 
                  fontSize: '13px', 
                  color: '#409eff', 
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => {
                  setSearchText('');
                  setSelectedProvider('all');
                  setSelectedType('all');
                  setSelectedBilling('all');
                }}
              >
                清除筛选
              </span>
            )}
          </div>

          {/* 模型卡片网格 */}
          <Row gutter={[16, 16]}>
            {filteredModels.map((model, index) => (
              <Col key={model.id || index} xs={24} sm={12} md={12} lg={8} xl={8}>
                <Card
                  style={{ 
                    borderRadius: '8px',
                    border: '1px solid #e4e7ed',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    height: '100%',
                    transition: 'all 0.3s ease'
                  }}
                  hoverable
                >
                  {/* 模型名称和供应商 */}
                  <div style={{ marginBottom: '16px' }}>
                    <h3 style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '16px', 
                      fontWeight: '600',
                      color: '#303133',
                      lineHeight: 1.4
                    }}>
                      {model.model_name}
                    </h3>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      background: '#ecf5ff',
                      color: '#409eff',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {model.provider}
                    </span>
                  </div>
                  
                  {/* 模型描述 */}
                  {model.description && (
                    <p style={{ 
                      margin: '0 0 16px 0',
                      fontSize: '13px',
                      color: '#909399',
                      lineHeight: 1.6
                    }}>
                      {model.description}
                    </p>
                  )}

                  {/* 价格信息 */}
                  <div style={{ 
                    marginTop: '16px', 
                    padding: '12px', 
                    background: '#f5f7fa', 
                    borderRadius: '8px',
                    border: '1px solid #e4e7ed'
                  }}>
                    {model.billing_type === 'token' && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span style={{ color: '#606266' }}>
                          <strong style={{ color: '#303133' }}>输入:</strong> ${model.input_price}/1M tokens
                        </span>
                        <span style={{ color: '#606266' }}>
                          <strong style={{ color: '#303133' }}>输出:</strong> ${model.output_price}/1M tokens
                        </span>
                      </div>
                    )}
                    {model.billing_type === 'request' && (
                      <span style={{ color: '#606266', fontSize: '13px' }}>
                        <strong style={{ color: '#303133' }}>价格:</strong> ${model.input_price}/次
                      </span>
                    )}
                    {model.billing_type === 'second' && (
                      <span style={{ color: '#606266', fontSize: '13px' }}>
                        <strong style={{ color: '#303133' }}>价格:</strong> ${model.input_price}/秒
                      </span>
                    )}
                  </div>

                  {/* 标签 */}
                  <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '4px 10px',
                      background: model.billing_type === 'token' ? '#ecf5ff' : 
                                 model.billing_type === 'request' ? '#f0f9ff' : '#fef3e2',
                      color: model.billing_type === 'token' ? '#409eff' : 
                             model.billing_type === 'request' ? '#67c23a' : '#e6a23c',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {getBillingTypeText(model.billing_type)}
                    </span>
                    <span style={{
                      padding: '4px 10px',
                      background: '#f5f7fa',
                      color: '#909399',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {model.endpoint_type}
                    </span>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}
    </div>
  );
};

export default Models;
